<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->

<template>
  <!-- //! nordy -->
  <div class="flex w-full flex-col items-center gap-4 p-4">
    <div class="mr-auto flex w-fit items-center justify-end gap-2">
      <button
        class="cursor-pointer rounded-md border border-none bg-blue-500 px-4 py-2 text-[14px] text-white hover:bg-blue-700"
        @click="toggleEditMode"
      >
        {{ editMode ? 'Cancel' : 'Edit' }}
      </button>
      <button
        v-if="editMode"
        class="cursor-pointer rounded-md border border-blue-500 bg-transparent px-4 py-2 text-[14px] text-blue-500 hover:border-blue-700 hover:text-blue-700"
        @click="selectCurrentPageItems"
      >
        Select Current Page Items
      </button>
      <button
        v-if="editMode"
        class="cursor-pointer rounded-md border-none bg-red-500 px-4 py-2 text-[14px] text-white hover:bg-red-700 disabled:opacity-50"
        :disabled="selectedItems.length === 0"
        @click="deleteSelectedItems"
      >
        Delete ({{ selectedItems.length }})
      </button>
    </div>

    <LoadingSuspense :is-loading="isFetching || isRefetching">
      <ul class="grid w-full grid-cols-6 gap-4">
        <li
          v-for="item in data?.items || []"
          :key="item._id || item.id"
          class="group relative flex size-30 list-none flex-col overflow-hidden hover:bg-gray-800"
          style="cursor: pointer"
          @click="editMode ? setSelectedItems(item, $event) : selectItem(item)"
        >
          <Checkbox
            v-if="editMode"
            v-model="selectedItems"
            class="absolute top-0 left-0"
            :value="item"
          />
          <div class="flex grow items-center justify-center">
            <template v-if="item.asset.kind === AssetKind.Audio">
              <div class="flex flex-col items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="size-10 text-gray-400"
                >
                  <path
                    d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 0 0 1.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06ZM18.584 5.106a.75.75 0 0 1 1.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 0 1-1.06-1.06 8.25 8.25 0 0 0 0-11.668.75.75 0 0 1 0-1.06Z"
                  />
                  <path
                    d="M15.932 7.757a.75.75 0 0 1 1.061 0 6 6 0 0 1 0 8.486.75.75 0 0 1-1.06-1.061 4.5 4.5 0 0 0 0-6.364.75.75 0 0 1 0-1.06Z"
                  />
                </svg>
                <span
                  v-if="item.asset.metadata?.duration != null"
                  class="text-[11px] text-gray-400"
                >
                  {{ formatDuration(item.asset.metadata.duration) }}
                </span>
              </div>
            </template>
            <!-- !nordy - 3D 모델은 이미지로 렌더 불가하므로 큐브 아이콘으로 대체 -->
            <template v-else-if="item.asset.kind === AssetKind.Model3D">
              <div class="flex flex-col items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  class="size-10 text-gray-400"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
                  />
                </svg>
              </div>
            </template>
            <img
              v-else
              :src="
                fileUrlByAsset({ asset: item.asset, quality: 'w200' }) ??
                undefined
              "
              :alt="item.asset.name"
              class="pointer-events-none max-h-25 max-w-full object-scale-down select-none"
            />
          </div>

          <div class="w-full px-1">
            <span
              class="block truncate text-[12px] group-hover:underline"
              :title="item.asset.name"
            >
              {{ item.asset.name }}
            </span>
          </div>
        </li>
      </ul>
    </LoadingSuspense>

    <div class="paginator-container">
      <Paginator
        :first="(currentPage - 1) * viewCount"
        :rows="viewCount"
        :total-records="data?.pagination?.totalCount"
        @page="onPageChange"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { isAxiosError } from 'axios'
import Checkbox from 'primevue/checkbox'
import Paginator from 'primevue/paginator'
import { computed, ref } from 'vue'

import { nordyApi } from '@/nordy/api'
import LoadingSuspense from '@/nordy/LoadingSuspense.vue'
import { useNordyUserStore } from '@/nordy/store/nordyUserStore'
import { useToastStore } from '@/platform/updates/common/toastStore'

import { AssetKind } from '../interfaces'
import type { ImageAsset } from '../interfaces'
import type { InputAsset } from '../interfaces/inputAsset.interface'
import { fileUrlByAsset } from '../utils/fileBucketUrl'

interface PaginationInfo {
  currentPage: number
  totalPages: number
  totalCount: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
}

interface PaginatedResponse {
  items: InputAsset<string, ImageAsset>[]
  pagination: PaginationInfo
}

const emit = defineEmits(['item-selected'])
const { assetKind } = defineProps<{
  assetKind: AssetKind
}>()

const message = useToastStore()

const selectedItems = ref<InputAsset[]>([])
const setSelectedItems = (input: InputAsset, e?: Event) => {
  e?.stopPropagation()
  e?.preventDefault()

  if (selectedItems.value.includes(input)) {
    selectedItems.value = selectedItems.value.filter((i) => i._id !== input._id)
  } else {
    selectedItems.value.push(input)
  }
}

const selectCurrentPageItems = () => {
  const currentPageItems = data.value?.items

  const unselectedItems =
    currentPageItems?.filter((item) => !selectedItems.value.includes(item)) ||
    []

  selectedItems.value.push(...unselectedItems)
}

const deleteSelectedItems = async () => {
  if (!confirm('Are you sure you want to delete these items?')) return

  try {
    await nordyApi.deleteInputImages({
      ids: selectedItems.value.map((i) => i._id)
    })
    await refetch()
    currentPage.value = 1
    selectedItems.value = []
    editMode.value = false
    message.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Deleted items',
      life: 2000
    })
  } catch (error) {
    console.error(`deleteSelectedItems error :`, error)
    alert('Failed to delete items')
  }
}

const editMode = ref(false)
const toggleEditMode = () => {
  editMode.value = !editMode.value
  selectedItems.value = []
}

const currentPage = ref(1)
const viewCount = ref(30)
const userStore = useNordyUserStore()

const { data, isFetching, refetch, isRefetching } = useQuery<PaginatedResponse>(
  {
    queryKey: [
      '/v1/input-asset/my',
      computed(() => userStore.nordyUser?._id),
      currentPage,
      computed(() => assetKind)
    ],
    retry: (failureCount, error) => {
      if (!isAxiosError(error)) return false
      const status = error.response?.status
      return failureCount < 3 && (status === undefined || status >= 500)
    },
    queryFn: async () => {
      return await nordyApi.getInputAssets({
        currentPage: currentPage.value,
        viewCount: viewCount.value,
        assetKind
      })
    }
  }
)

function onPageChange(event: { page: number }) {
  currentPage.value = event.page + 1
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function selectItem(item: InputAsset) {
  emit('item-selected', item)
}
</script>
